// CareLogix™ ORACLE — Member AuthContext (Phase 23).
//
// Provides app-wide session state for the public ORACLE app. Does NOT touch
// AdminOne which keeps its own JWT scope.
//
// Race-condition rule (per Emergent Auth playbook):
//   If `window.location.hash` contains `session_id=`, we SKIP the /me check
//   here and let <AuthCallback> exchange the session_id first.
//
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE EMERGENT GOOGLE AUTH FLOW.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { authApi, authStorage, isLikelyRestrictedWebview, googleSignInUrl } from "@/api/authClient";

const AuthContext = createContext({
  user: null,
  loading: true,
  isGuest: false,
  isAuthenticated: false,
  tier: "guest",
  tierRank: 0,
  signIn: async () => null,
  signUp: async () => null,
  signInGoogle: () => {},
  signInComingSoon: async () => null,
  startGuest: async () => null,
  exchangeGoogleSession: async () => null,
  signOut: async () => null,
  refresh: async () => null,
  isRestrictedWebview: false,
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(authStorage.getUser());
  const [loading, setLoading] = useState(true);
  const isRestrictedWebview = useMemo(() => isLikelyRestrictedWebview(), []);

  const applySessionPayload = useCallback((payload) => {
    if (!payload) return null;
    if (payload.session_token) authStorage.setToken(payload.session_token);
    if (payload.user)          authStorage.setUser({ ...payload.user, guest: !!payload.guest });
    setUser(payload.user ? { ...payload.user, guest: !!payload.guest } : null);
    return payload;
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await authApi.me();
      const u = { ...(data.user || {}), guest: !!data.guest };
      authStorage.setUser(u);
      setUser(u);
      return u;
    } catch (e) {
      authStorage.clear();
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    // CRITICAL: skip /me check if we're handling an OAuth callback. The
    // <AuthCallback> route will exchange `session_id` and establish the session.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return () => { cancelled = true; };
    }
    // No stored token AND no session cookie (we can only feature-detect the
    // token) → user is anonymous; skip the /me probe to avoid an expected 401
    // showing up as console noise on every public page load.
    if (!authStorage.getToken()) {
      setUser(null);
      setLoading(false);
      return () => { cancelled = true; };
    }
    (async () => {
      try {
        const { data } = await authApi.me();
        if (cancelled) return;
        const u = { ...(data.user || {}), guest: !!data.guest };
        authStorage.setUser(u);
        setUser(u);
      } catch {
        if (cancelled) return;
        authStorage.clear();
        setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const signIn = useCallback(async (email, password) => {
    const { data } = await authApi.login({ email, password });
    return applySessionPayload(data);
  }, [applySessionPayload]);

  const signUp = useCallback(async (email, password, name) => {
    const { data } = await authApi.register({ email, password, name: name || "" });
    return applySessionPayload(data);
  }, [applySessionPayload]);

  const startGuest = useCallback(async () => {
    const { data } = await authApi.guest();
    return applySessionPayload(data);
  }, [applySessionPayload]);

  const signInGoogle = useCallback(() => {
    // Per Emergent Auth playbook — synchronous redirect, no preflight required.
    window.location.href = googleSignInUrl();
  }, []);

  const signInComingSoon = useCallback(async (provider, identifier) => {
    const { data } = await authApi.oauthComingSoon(provider, identifier);
    return data;
  }, []);

  const exchangeGoogleSession = useCallback(async (session_id) => {
    const { data } = await authApi.googleSession(session_id);
    return applySessionPayload(data);
  }, [applySessionPayload]);

  const signOut = useCallback(async () => {
    try { await authApi.logout(); } catch { /* ignore */ }
    authStorage.clear();
    setUser(null);
    return true;
  }, []);

  const isGuest = !!user && !!user.guest;
  const isAuthenticated = !!user && !user.guest;
  const tier = user?.tier || (isGuest ? "guest" : "guest");
  const tierRank = user?.tier_rank ?? 0;

  const value = useMemo(() => ({
    user, loading, isGuest, isAuthenticated, tier, tierRank,
    signIn, signUp, signInGoogle, signInComingSoon, startGuest,
    exchangeGoogleSession, signOut, refresh, isRestrictedWebview,
  }), [
    user, loading, isGuest, isAuthenticated, tier, tierRank,
    signIn, signUp, signInGoogle, signInComingSoon, startGuest,
    exchangeGoogleSession, signOut, refresh, isRestrictedWebview,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

export default AuthContext;
